import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_BASE } from '../../config';
import ProfileTab from '../tabs/ProfileTab';
import ApiKeysTab from '../tabs/ApiKeysTab';
import AuditLogsTab from '../tabs/AuditLogsTab';
import BillingTab from '../tabs/BillingTab';
import Select from 'react-select';
import './TenantDetail.css';

const TenantDetail = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [tenant, setTenant] = useState(null);
  const [tab, setTab] = useState('profile');
  const [tenantList, setTenantList] = useState([]);

  // 🔁 Load specific tenant info
  useEffect(() => {
    if (!tenantId || !token) return;

    const fetchTenant = async () => {
      try {
        const res = await fetch(`${API_BASE}/admin/tenants/${tenantId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          setTenant(null);
          return;
        }

        const text = await res.text();
        const data = text ? JSON.parse(text) : null;
        setTenant(data);
      } catch (err) {
        console.error("Error fetching tenant:", err);
        setTenant(null);
      }
    };

    fetchTenant();
  }, [tenantId, token]);

  // 🔁 Load all tenants for the dropdown
  useEffect(() => {
    if (!token) return;

    const fetchAllTenants = async () => {
      try {
        const res = await fetch(`${API_BASE}/admin/tenants`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (Array.isArray(data.content)) {
          setTenantList(data.content);
        } else {
          console.warn("Expected data.content to be array but got:", data);
          setTenantList([]);
        }
      } catch (err) {
        console.error("Failed to fetch tenant list:", err);
        setTenantList([]);
      }
    };

    fetchAllTenants();
  }, [token]);

  // 🔁 Handle tenant switch
  const handleTenantChange = (selectedOption) => {
    if (selectedOption?.value) {
      // 🔁 Navigating triggers useEffect which re-fetches tenant data
      navigate(`/admin/tenants/${selectedOption.value}`);
      setTab('profile'); // Optional: reset to Profile tab on switch
    }
  };

  // 🔁 Map dropdown options
  const tenantOptions = tenantList.map((t) => ({
    value: t.id,
    label: t.email,
  }));

  const selectedTenantOption = tenantOptions.find(
    (opt) => opt.value.toString() === tenantId?.toString()
  );

  return (
    <div className="tenant-detail-container">
      <div className="tenant-heading-wrapper">
        <h2 className="tenant-heading">
          🧾 Tenant: <span>{tenant?.email || 'Loading...'}</span>
        </h2>

        {/* 🔽 Dropdown to switch tenants */}
        <div className="tenant-switcher-select">
          <Select
            options={tenantOptions}
            value={selectedTenantOption}
            onChange={handleTenantChange}
            placeholder="Switch Tenant..."
            classNamePrefix="react-select"
            isSearchable
          />
        </div>
      </div>

      {/* 🔘 Tab Navigation */}
      <div className="tab-bar">
        {['profile', 'keys', 'logs', 'billing'].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`tab-button ${tab === t ? 'active' : ''}`}
          >
            {t.toUpperCase()}
          </button>
        ))}
      </div>

      {/* 📦 Tab Content */}
      <div className="tab-content">
        {tab === 'profile' && <ProfileTab key={`profile-${tenantId}`} tenant={tenant} />}
        {tab === 'keys' && <ApiKeysTab key={`keys-${tenantId}`} tenantId={tenantId} />}
        {tab === 'logs' && <AuditLogsTab key={`logs-${tenantId}`} tenantId={tenantId} />}
        {tab === 'billing' && <BillingTab key={`billing-${tenantId}`} tenantId={tenantId} />}
      </div>
    </div>
  );
};

export default TenantDetail;
